import React, { memo, useCallback } from "react";
import { SuggestiveTyping } from "@swiggy-private/dls-web-recipe-food-web/dist/components/SuggestiveTyping";
import { isEmpty } from "@swiggy-private/js-utils";
import type { IFieldOption } from "../Interface/fieldRendererInterface";
import { getSuggestiveFieldTestId } from "@payment-ui/helpers/SuggestiveFieldTestId";

type FieldOption = IFieldOption;

interface SuggestiveTypingFieldProps {
    id: string;
    label: string;
    enabled: boolean;
    value: string;
    sortedOptions: Array<{
        text: string | React.ReactNode;
        id: number;
        data: {
            suggestion: string;
            value: string;
            uniqueId: string;
        };
        secondaryText?: string | React.ReactNode;
    }>;
    isLoading: boolean;
    inputRef: React.RefObject<HTMLInputElement>;
    allOptions: FieldOption[];
    apiCall?: string;
    onChangeValue: (fieldId: string, value: string, allOptions: FieldOption[], apiCall?: string) => void;
    onClick: (fieldId: string, data?: Record<string, unknown>) => void;
    isDetailsSubmitted?: boolean;
    emptyResultsContent?: React.ReactNode;
}

const SuggestiveTypingFieldComponent: React.FC<SuggestiveTypingFieldProps> = ({
    id,
    label,
    value,
    sortedOptions,
    isLoading,
    inputRef,
    allOptions,
    apiCall,
    onChangeValue,
    onClick,
    isDetailsSubmitted,
    emptyResultsContent,
}) => {
    const handleChange = useCallback(
        (newValue: string) => onChangeValue(id, newValue, allOptions, apiCall),
        [id, onChangeValue, allOptions, apiCall],
    );

    const handleClick = useCallback((data?: Record<string, unknown>) => onClick(id, data), [id, onClick]);

    return (
        <SuggestiveTyping
            key={id}
            value={value}
            name={id}
            options={sortedOptions}
            label={label}
            onChangeValue={handleChange}
            onClick={handleClick}
            inputRef={inputRef}
            containerHeight={!isEmpty(sortedOptions) ? 200 : undefined}
            emptyResultsContent={emptyResultsContent}
            attrs={{
                disabled: isLoading || isDetailsSubmitted,
                autoComplete: "off",
                "data-testid": getSuggestiveFieldTestId(id),
            }}
        />
    );
};

export default memo(SuggestiveTypingFieldComponent);
